import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BreadCrumb from "../Controls/BreadCrumb";
import LocationListItem from "../ListItems/LocationListItem";
import PersonAgreementListItem from "../ListItems/PersonAgreementListItem";
import ContactInfoListItem from "../ListItems/ContactInfoListItem";
import CertificationListItem from "../ListItems/CertificationListItem";
import EducationListItem from "../ListItems/EducationListItem";
import EmployerPreferenceListItem from "../ListItems/EmployerPreferenceListItem";
import LocationEditModal from "../Modals/LocationEditModal";
import AgreementDetailsModal from "../Modals/AgreementDetailsModal";
import ContactInfoEditModal from "../Modals/ContactInfoEditModal";
import EducationEditModal from "../Modals/EducationEditModal";
import CertificationEditModal from "../Modals/CertificationEditModal";
import EmployerPrefEditModal from "../Modals/EmployerPrefEditModal";
import UserData from "../Controls/UserData";
import Help from "../Controls/Help";
import { FaUser } from "react-icons/fa";
import { MdAddBox } from "react-icons/md";
import { toast } from "react-toastify";
import API from "@src/API.js";
import { APPEARANCE_MODES } from "../../theme/appearance";
import { useTheme } from "../../theme/ThemeProvider";

export default function AccountView() {
   const [userData, setUserData] = useState(() => UserData.UserData() || {});
   const [user] = useState(UserData.User());
   const [locationModalId, setLocationModalId] = useState(null);
   const [agreementModal, setAgreementModal] = useState(null);
   const [contactInfoModalId, setContactInfoModalId] = useState(null);
   const [educationModalId, setEducationModalId] = useState(null);
   const [certificationModalId, setCertificationModalId] = useState(null);
   const [employerPrefModalId, setEmployerPrefModalId] = useState(null);
   const [savingAppearance, setSavingAppearance] = useState(false);
   const { appearanceMode, setAppearanceMode } = useTheme();
   const navigate = useNavigate();
   const personEdit = () => {
      navigate("/personEdit/" + user.userId);
   };
   function refreshUserData() {
      setUserData(UserData.UserData() || {});
   }
   async function onAppearanceChange(mode) {
      setAppearanceMode(mode);
      setSavingAppearance(true);
      try {
         const resp = await API.postAsync("/Person/AppearanceMode", { appearanceMode: mode });
         if (!resp?.ok) {
            toast.error(resp?.jsonData?.message || resp?.message || "Could not sync appearance to your account");
         } else {
            const data = UserData.UserData() || {};
            if (data.person) {
               data.person.appearanceMode = mode;
               data.person.AppearanceMode = mode;
               window.sessionStorage.setItem("UserData", JSON.stringify(data));
            }
         }
      } catch {
         toast.error("Could not sync appearance to your account");
      } finally {
         setSavingAppearance(false);
      }
   }
   const closeLocationModal = () => {
      setLocationModalId(null);
      refreshUserData();
   };
   const closeAgreementModal = () => setAgreementModal(null);
   const closeContactInfoModal = () => {
      setContactInfoModalId(null);
      refreshUserData();
   };
   const closeEducationModal = () => {
      setEducationModalId(null);
      refreshUserData();
   };
   const closeCertificationModal = () => {
      setCertificationModalId(null);
      refreshUserData();
   };
   const closeEmployerPrefModal = () => {
      setEmployerPrefModalId(null);
      refreshUserData();
   };
   return (
      <div className="pagebody">
         <BreadCrumb props={["Account"]} />
         <div className="tabbody">
            <h2>My Account</h2>
            <span className="listTitleIcons">
               <FaUser onClick={personEdit} />
            </span>
            <div className="tabsection">
               <div className="listTitle">
                  <span>Appearance</span>
               </div>
               <fieldset className="appearanceMode" disabled={savingAppearance}>
                  <legend className="sr-only">Appearance mode</legend>
                  {APPEARANCE_MODES.map((mode) => (
                     <label key={mode} className="appearanceModeOption">
                        <input
                           type="radio"
                           name="appearanceMode"
                           value={mode}
                           checked={appearanceMode === mode}
                           onChange={() => onAppearanceChange(mode)}
                        />
                        <span>{mode.charAt(0).toUpperCase() + mode.slice(1)}</span>
                     </label>
                  ))}
               </fieldset>
               <p className="appearanceModeHint">
                  System follows your browser or device preference.
               </p>
            </div>
            <div className="tabsection">
               <div className="listTitle">
                  <span>
                     My Locations
                     <Help id="2128" />
                  </span>
                  <div className="listTitleIcons">
                     <MdAddBox
                        data-testid="location-add"
                        role="button"
                        aria-label="Add location"
                        onClick={() => setLocationModalId(0)}
                     />
                  </div>
               </div>
               <ul>
                  {(userData.locations || []).map((l) => {
                     return (
                        <LocationListItem
                           key={l.ID}
                           location={l}
                           onEdit={(id) => setLocationModalId(id)}
                        />
                     );
                  })}
               </ul>
            </div>
            {locationModalId !== null ? (
               <LocationEditModal locationId={locationModalId} onClose={closeLocationModal} />
            ) : null}
            <div className="tabsection">
               <div className="listTitle">
                  <span>
                     Agreements
                     <Help id="2129" />
                  </span>
                  <div className="listTitleIcons"></div>
               </div>
               <ul>
                  {(userData.agreements || []).map((a) => {
                     return (
                        <PersonAgreementListItem
                           key={a.ID ?? a.AgreementID}
                           agreement={a}
                           onView={(agr) => setAgreementModal(agr)}
                        />
                     );
                  })}
               </ul>
            </div>
            {agreementModal ? (
               <AgreementDetailsModal agreement={agreementModal} onClose={closeAgreementModal} />
            ) : null}
            <div className="tabsection">
               <div className="listTitle">
                  {/* <p><Link to={'/personEdit/'+user.userId}>Personal Data</Link></p> */}
                  <span>
                     Contact Info
                     <Help id="2130" />
                  </span>
                  <div className="listTitleIcons">
                     <MdAddBox
                        data-testid="contact-info-add"
                        role="button"
                        aria-label="Add contact info"
                        onClick={() => setContactInfoModalId(0)}
                     />
                  </div>
               </div>
               <ul>
                  {(userData.contactInfos || []).map((ci) => {
                     return (
                        <ContactInfoListItem
                           key={ci.ID}
                           contactInfo={ci}
                           onEdit={(id) => setContactInfoModalId(id)}
                        />
                     );
                  })}
               </ul>
            </div>
            {contactInfoModalId !== null ? (
               <ContactInfoEditModal
                  contactInfoId={contactInfoModalId}
                  onClose={closeContactInfoModal}
               />
            ) : null}

            <div className="tabsection">
               <div className="listTitle">
                  <span>
                     Education ({(userData.education || []).length})
                     <Help id="2131" />
                  </span>
                  <div className="listTitleIcons">
                     <MdAddBox
                        data-testid="education-add"
                        role="button"
                        aria-label="Add education"
                        onClick={() => setEducationModalId(0)}
                     />
                  </div>
               </div>
               <ul>
                  {(userData.education || []).map((edu) => {
                     return (
                        <li key={edu.ID || edu.id}>
                           <EducationListItem
                              education={edu}
                              onEdit={(id) => setEducationModalId(id)}
                           />
                        </li>
                     );
                  })}
               </ul>
            </div>
            {educationModalId !== null ? (
               <EducationEditModal
                  educationId={educationModalId}
                  onClose={closeEducationModal}
               />
            ) : null}

            <div className="tabsection">
               <div className="listTitle">
                  <span>
                     Certifications ({(userData.certifications || []).length})<Help id="2131" />
                  </span>
                  <div className="listTitleIcons">
                     <MdAddBox
                        data-testid="certification-add"
                        role="button"
                        aria-label="Add certification"
                        onClick={() => setCertificationModalId(0)}
                     />
                  </div>
               </div>
               <ol>
                  {(userData.certifications || []).map((cert) => {
                     return (
                        <CertificationListItem
                           key={cert.ID}
                           certification={cert}
                           onEdit={(id) => setCertificationModalId(id)}
                        />
                     );
                  })}
               </ol>
            </div>
            {certificationModalId !== null ? (
               <CertificationEditModal
                  certificationId={certificationModalId}
                  onClose={closeCertificationModal}
               />
            ) : null}
            <div className="tabsection">
               <div className="listTitle">
                  <span>Feedback &amp; bugs</span>
               </div>
               <p>
                  <Link to="/feedback" data-testid="feedback-link">
                     Send feedback or report a bug
                  </Link>
               </p>
            </div>
            <div className="tabsection">
               <div className="listTitle">
                  <span>
                     Employer Preferences
                     <Help id="2132" />
                  </span>
                  <div className="listTitleIcons">
                     <MdAddBox
                        data-testid="employer-pref-add"
                        role="button"
                        aria-label="Add employer preference"
                        onClick={() => setEmployerPrefModalId(0)}
                     />
                  </div>
               </div>
               <ol>
                  {(userData.employerPreferences || []).map((pref) => {
                     return (
                        <EmployerPreferenceListItem
                           key={pref.ID}
                           preferences={pref}
                           onEdit={(id) => setEmployerPrefModalId(id)}
                        />
                     );
                  })}
               </ol>
            </div>
            {employerPrefModalId !== null ? (
               <EmployerPrefEditModal
                  preferenceId={employerPrefModalId}
                  onClose={closeEmployerPrefModal}
               />
            ) : null}
         </div>
      </div>
   );
}


